import { getGeoData } from './geo-location';

/**
 * Auto-select country in one or more <select> elements based on GeoIP lookup.
 * Matches by data-country-code first, then option label.
 * Works with native selects and Select2.
 *
 * @param {string[]|NodeList|HTMLElement[]} selectors
 */
async function autoSelectNationality(selectors = []) {
    if (!selectors || selectors.length === 0) {
        return;
    }

    const data = await getGeoData();
    const countryCode = data?.country_code;
    const countryName = data?.country_name;

    const elements =
        typeof selectors[0] === 'string'
            ? selectors.map((s) => document.querySelector(s)).filter(Boolean)
            : Array.from(selectors);

    elements.forEach((select) => {
        applyGeoToSelect(select, countryCode, countryName, false);
    });
}

function applyGeoToSelect(select, countryCode, countryName, silent) {
    if (!select || select.tagName !== 'SELECT' || select.value) {
        return false;
    }

    let match = null;
    const options = Array.from(select.options);

    if (countryCode) {
        match = options.find(
            (opt) => opt.dataset.countryCode?.toLowerCase() === countryCode.toLowerCase()
        );
    }

    if (!match && countryName) {
        match = options.find(
            (opt) => opt.text.trim().toLowerCase() === countryName.trim().toLowerCase()
        );
    }

    if (!match) {
        return false;
    }

    match.selected = true;
    select.value = match.value;

    if (silent) {
        if (window.jQuery && jQuery(select).hasClass('select2-hidden-accessible')) {
            jQuery(select).val(match.value);
            const $rendered = jQuery(select).next('.select2-container').find('.select2-selection__rendered');
            if ($rendered.length) {
                $rendered.text(match.text).attr('title', match.text);
            }
        }
        return true;
    }

    if (window.jQuery && jQuery.fn.select2 && jQuery(select).hasClass('select2-hidden-accessible')) {
        jQuery(select).val(match.value).trigger('change.select2');
    } else {
        select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    return true;
}

/**
 * GeoIP-preselect a country field that may still be a <select> or already upgraded
 * to option buttons. Only sets value / .is-selected — does not change markup or styling.
 *
 * @param {ParentNode} root
 * @param {{ silent?: boolean }} [options]
 * @returns {Promise<boolean>}
 */
async function autoSelectCountryInContainer(root, options = {}) {
    if (!root) {
        return false;
    }

    const silent = options.silent === true;
    const data = await getGeoData();
    const countryCode = data?.country_code;
    const countryName = data?.country_name;
    if (!countryCode && !countryName) {
        return false;
    }

    const select =
        root.querySelector?.('select[data-country-select], select[data-preselect-by-ip], #nationality') ||
        null;
    if (select && !select.value) {
        return applyGeoToSelect(select, countryCode, countryName, silent);
    }

    const hidden = root.querySelector?.('input.landing-embed-option-value');
    const group = root.querySelector?.('.landing-embed-option-buttons');
    if (!hidden || !group || (hidden.value && String(hidden.value).trim() !== '')) {
        return false;
    }

    const buttons = Array.from(group.querySelectorAll('.landing-embed-option-btn'));
    let match = null;
    if (countryCode) {
        match = buttons.find(
            (btn) =>
                String(btn.getAttribute('data-country-code') || '').toLowerCase() ===
                countryCode.toLowerCase()
        );
    }
    if (!match && countryName) {
        const name = countryName.trim().toLowerCase();
        match = buttons.find((btn) => btn.textContent.trim().toLowerCase() === name);
    }
    if (!match) {
        return false;
    }

    const value = match.getAttribute('data-value') || '';
    buttons.forEach((btn) => btn.classList.remove('is-selected'));
    match.classList.add('is-selected');
    hidden.value = value;
    if (!silent) {
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return true;
}

window.autoSelectNationality = autoSelectNationality;
window.autoSelectCountryInContainer = autoSelectCountryInContainer;
